import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ExternalLink, Play } from 'lucide-react';
import { getSettings } from '../utils/settingsCache';

const FALLBACK_EMBED_URL = 'https://dexscreener.com/solana/8qxx7bunaemun2eamasnqmkkyc3lkkvrwpc6xibjkbds?embed=1&theme=dark&trades=0&info=0';
const FALLBACK_EXT_URL   = 'https://dexscreener.com/solana/8qxx7bunaemun2eamasnqmkkyc3lkkvrwpc6xibjkbds';

const PriceChart = () => {
  // ── All state / refs ───────────────────────────────────────────────────────
  const [activeChart,    setActiveChart]    = useState('dexscreener');
  const [isChartLoaded,  setIsChartLoaded]  = useState(false);
  const [shouldLoadChart,setShouldLoadChart]= useState(false);
  const [sectionHidden,  setSectionHidden]  = useState(false);
  const [chartConfig,    setChartConfig]    = useState({ name: 'DEXScreener', embedUrl: FALLBACK_EMBED_URL, externalUrl: FALLBACK_EXT_URL });
  const chartContainerRef = useRef(null);

  // Mobile detection — iframe causes iOS Safari white screen (WebGL GPU memory spike)
  const isMobile = useMemo(() => window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);

  // ── Compute chart options from config (used in useEffect below) ────────────
  const defaultChartOptions = [
    { id: 'dexscreener', name: chartConfig.name, description: 'Real-time DEX trading data', url: chartConfig.externalUrl, embedUrl: chartConfig.embedUrl, icon: 'TrendingUp', gradient: 'from-green-500 to-cyan-500', order: 1, active: true }
  ];
  const chartOptions = defaultChartOptions
    .filter(link => link.active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(link => ({ ...link, icon: TrendingUp }));

  // ── All useEffects (MUST be before any early return) ──────────────────────
  useEffect(() => {
    getSettings().then(data => {
      if (!data) return;
      if (data.section_chart === 'hidden') { setSectionHidden(true); return; }
      setChartConfig({
        name:        data.chart_name         || 'DEXScreener',
        embedUrl:    data.chart_embed_url    || FALLBACK_EMBED_URL,
        externalUrl: data.chart_external_url || FALLBACK_EXT_URL,
      });
    });
  }, []);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadChart) { setShouldLoadChart(true); observer.disconnect(); }
        });
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(chartContainerRef.current);
    return () => observer.disconnect();
  }, [shouldLoadChart]);

  useEffect(() => {
    if (chartOptions.length > 0 && !chartOptions.find(chart => chart.id === activeChart)) {
      setActiveChart(chartOptions[0].id);
    }
  }, [chartOptions, activeChart]);

  // ── Early return after ALL hooks ───────────────────────────────────────────
  if (sectionHidden) return null;
  // Mobile: DexScreener button already exists above — hide entire chart section
  if (isMobile) return null;

  const activeChartData = chartOptions.find(chart => chart.id === activeChart) || chartOptions[0];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Live{' '}
            <span className="bg-gradient-to-r from-solana-primary to-neon-cyan bg-clip-text text-transparent">
              $SVB
            </span>{' '}
            Price Chart
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Track SOLVIBE's real-time price movements and trading data across Solana DEXs
          </p>
        </motion.div>

        {/* Chart Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl p-2 flex gap-2">
            {chartOptions.map((chart) => (
              <button
                key={chart.id}
                onClick={() => setActiveChart(chart.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeChart === chart.id
                    ? 'bg-gradient-to-r from-solana-primary to-neon-cyan text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <chart.icon className="w-5 h-5" />
                {chart.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Chart Container */}
        {activeChartData && (
          <motion.div
            ref={chartContainerRef}
            data-no-tilt
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`bg-gradient-to-br ${activeChartData.gradient} rounded-full w-10 h-10 flex items-center justify-center`}>
                    <activeChartData.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{activeChartData.name}</h3>
                    <p className="text-gray-400 text-sm">{activeChartData.description}</p>
                  </div>
                </div>
                <a
                  href={activeChartData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${activeChartData.name} in full view`}
                  className="flex items-center gap-2 bg-gradient-to-r from-gray-700 to-gray-600 text-white py-2 px-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Full View
                </a>
              </div>
            </div>
            
            <div className="aspect-video relative">
              {isMobile ? (
                // Mobile: no iframe — WebGL chart causes iOS Safari GPU memory spike → white screen
                // Show a clean link card instead
                <div className="w-full h-full bg-gray-900/50 flex flex-col items-center justify-center gap-5 px-8 py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-solana-primary to-neon-cyan rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold text-lg mb-1">Live $SVB Chart</p>
                    <p className="text-gray-400 text-sm">Best viewed on desktop — tap below to open full chart</p>
                  </div>
                  <a
                    href={activeChartData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-solana-primary to-neon-cyan text-white px-6 py-3 rounded-xl font-semibold text-sm w-full max-w-xs hover:opacity-90 transition-opacity"
                  >
                    <ExternalLink className="w-4 h-4 flex-shrink-0" />
                    Open Chart on DexScreener
                  </a>
                </div>
              ) : !shouldLoadChart ? (
                // Desktop skeleton until in view
                <div className="w-full h-full bg-gray-900/50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-solana-primary to-neon-cyan rounded-full flex items-center justify-center mb-4 mx-auto">
                      <TrendingUp className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-gray-400 text-sm mb-4">Chart will load when in view</p>
                    <div className="w-32 h-2 bg-gray-800 rounded-full mx-auto">
                      <div className="w-0 h-full bg-gradient-to-r from-solana-primary to-neon-cyan rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ) : (
                // Desktop: full iframe embed
                <iframe
                  src={activeChartData.embedUrl}
                  className="w-full h-full"
                  width="100%"
                  height="400"
                  frameBorder="0"
                  loading="lazy"
                  allowFullScreen
                  title={`${activeChartData.name} SOLVIBE Chart`}
                  onLoad={() => setIsChartLoaded(true)}
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Chart Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <div className="bg-black/30 backdrop-blur-sm border border-solana-primary/20 rounded-xl p-6 max-w-2xl mx-auto">
            <h4 className="text-lg font-semibold text-white mb-3">
              📊 Real-Time Trading Data
            </h4>
            <p className="text-gray-300 text-sm">
              Charts update automatically with live price data from Solana DEXs. Use these tools to analyze trends, 
              set trading strategies, and track SOLVIBE's market performance in real-time.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PriceChart;